<!--
  The hero.

  Most apps in this category open with a slogan in heavy type over a gradient,
  with a phone beside it. Nocturne rules out the first half: weight never goes
  above 500. The second half is a picture of the product rather than the
  product.

  So this opens with what the app actually tells you. The figure comes first,
  set the way the Today screen sets it, with the two numbers it came from
  underneath. 1,152 over 18 days is 64 a day, and you can check that.

  The headline used to be "Your money, on your phone.", which is where the
  data lives rather than what the reader gets. Privacy is why someone stays,
  not why they click. It now leads on the one number the app exists to
  produce, and the privacy rule moves to the sentence directly under it, still
  with its carve-out attached.

  The three setup steps used to sit in this column. They are their own section
  now, so the hero holds one idea and the fold has room for the trust row.

  The field is the band's three stop 170deg variant, the one the app uses when
  the band is the whole screen rather than a strip at the top of one. That is
  what a landing page is.

  Two equal columns. This was `[1fr_auto]`, which sized the figure column to
  its own content and handed every spare pixel to the words, so the two halves
  drifted apart with a dead gap between them. Equal columns give the figure the
  room the page has, and the figure scales to fill it.

  No em dashes in user-facing copy anywhere in this repo. See CLAUDE.md.
-->
<section class="band-full relative isolate overflow-hidden">
  <div
    class="mx-auto grid max-w-[1180px] gap-16 px-8 pb-[84px] pt-[132px] lg:grid-cols-2 lg:items-center lg:gap-24 lg:pb-[126px] lg:pt-[168px]"
  >
    <!-- The words. -->
    <div class="max-w-[46ch]">
      <!--
        The name, and what it is. The headline below is the answer the app
        gives, which is the right thing to lead with, but it never says
        "budget app". This line does, in plain words, where both a reader and
        a crawler look first. Same register as the kicker it replaced.
      -->
      <p
        class="text-kicker uppercase text-accent-300 reveal"
        appReveal
        i18n="@@heroKicker"
      >
        Ledgerly, a budget app for iPhone and Android
      </p>

      <!-- The answer the app gives you, in the reader's own words. -->
      <h1
        class="mt-kicker-gap text-headline text-neutral-100 text-balance sm:text-[42px] lg:text-[52px] reveal"
        appReveal
        i18n="@@heroHeadlineSpend"
      >
        See what you can spend today.
      </h1>

      <!--
        Binding copy. It states the rule and names the exception in the same
        breath, which is what makes it true. Do not trim the last clause.

        One deliberate change from the app's own `PromiseCopy.body`: the app
        says "on this device", because in the app "this device" IS the phone.
        On a web page read from a laptop, "this device" points at the laptop
        and the sentence becomes false. "your phone" says the same thing
        accurately here, and matches the app's own `onboardingHeroTitle`,
        which already says "this phone".

        The first sentence is new and says what the number is. The privacy
        meaning behind it is untouched: same rule, same backup carve-out, same
        scope. Nothing in the second sentence may move.
      -->
      <p
        class="mt-8 max-w-[52ch] text-body text-accent-200 reveal"
        appReveal
        i18n="@@heroPromiseSpend"
      >
        One number, worked out from what is left and the days until you are
        paid. Ledgerly keeps every figure on your phone. Nothing is sold, no
        account is required, and nothing is uploaded unless you switch on
        backup yourself.
      </p>

      <div class="mt-[42px]">
        <layout-store-badges location="hero" [onBand]="true" />
      </div>

      <!--
        The trust row. Every line here is a fact stated elsewhere in the app
        or this page, so nothing has to be invented to fill it. When the store
        listings have a real rating count, that number belongs here and these
        three move under it.
      -->
      <ul class="mt-8 flex flex-wrap gap-3">
        @for (chip of trustChips; track chip) {
        <li
          class="rounded-pill border border-neutral-100/20 px-3 py-1 text-kicker uppercase text-accent-200 reveal"
          [appReveal]="$index * 60"
        >
          {{ chip }}
        </li>
        }
      </ul>

      <p class="mt-6 text-sub text-accent-300">
        <span i18n="@@heroTrial">Free to start. Then it is a paid app.</span>
        <a
          href="#pricing"
          class="ml-2 underline decoration-accent-300/40 underline-offset-4 hover:decoration-accent-300"
          i18n="@@heroPricingLink"
          >See what it costs</a
        >
      </p>
    </div>

    <!-- The figure. -->
    <div
      class="relative w-full max-w-[520px] justify-self-start lg:justify-self-end reveal"
      appReveal
    >
      <!--
        The shield from the app's own promise screen: a 1.5px stroke at 9%
        alpha. It lives inside this column and is centred on it, so it frames
        the figure at every width. Anchored to the section instead, it drifted
        with the viewport edge and its outline ended up crossing the numerals,
        which reads as a mistake rather than as texture.
      -->
      <svg
        class="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 opacity-[0.09] lg:h-[620px] lg:w-[620px]"
        viewBox="0 0 200 200"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M100 14 40 40v56c0 38 26 68 60 90 34-22 60-52 60-90V40L100 14Z"
          stroke="var(--color-neutral-100)"
          stroke-width="1.5"
        />
      </svg>

      <p class="text-kicker uppercase text-accent-300" i18n="@@heroFigureKicker">
        Wednesday 29 July
      </p>

      <p
        class="mt-kicker-gap-today text-label text-accent-200 lg:text-[15px]"
        i18n="@@heroFigureLabel"
      >
        Safe to spend today
      </p>

      <!--
        46px in the app. This is the same figure at page scale. `tabular-nums`
        is inherited from `html`, so it can grow without reflowing.
      -->
      <p
        class="mt-4 whitespace-nowrap text-[clamp(46px,7.4vw,104px)] font-medium leading-[1.04] tracking-[-0.025em] text-neutral-100"
      >
        {{ safeToSpend }}
      </p>

      <!-- This hairline is --color-section-ghost, not the divider. -->
      <div
        class="mt-6 h-px w-full origin-left bg-section-ghost reveal-rule"
        appReveal
      ></div>

      <dl class="text-sub lg:text-[13px]">
        <div class="flex justify-between pt-3">
          <dt class="text-accent-200" i18n="@@heroArithLeft">
            Left this period
          </dt>
          <dd class="text-neutral-100">{{ left }}</dd>
        </div>
        <div class="flex justify-between pt-3">
          <dt class="text-accent-200" i18n="@@heroArithDays">Days remaining</dt>
          <dd class="text-neutral-100">18</dd>
        </div>
      </dl>

      <p
        class="mt-8 max-w-[40ch] text-sub leading-relaxed text-accent-300"
        i18n="@@heroArithNote"
      >
        Ledgerly shows you the sum, not just the answer. What is left, divided
        by the days you have left.
      </p>
    </div>
  </div>
</section>
